import { captureException } from '@sentry/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { type ModalFC } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Dialog } from '@/components/ui/dialog';
import { YStack } from '@/components/ui/layout/layout';
import TextArea from '@/components/ui/TextArea';
import { Feedback } from '@/features/feedback/feedback.types';
import { useHashRoute } from '@/hooks/useHashRoute';
import { cn } from '@/lib/utils';
import { userStore } from '@/stores/user.store';
import { submitSurvey, trackEvent } from '@/utils/analytics';
import { handleMailTo } from '@/utils/platform';

import { subscriptionApis, useSubscription } from './subscription.query';
import { type RetentionOffer } from './subscription.types';

const FALLBACK_RETENTION_OFFER: RetentionOffer = {
  display_amount: '50% off',
  duration_label: 'for the next 3 months',
};

type CancelStep = 'reason' | 'deflection' | 'final-feedback';
type CancellationReason =
  | 'too-expensive'
  | 'not-using-enough'
  | 'ai-quality'
  | 'missing-feature'
  | 'privacy-concerns'
  | 'technical-problems'
  | 'temporary-need'
  | 'other';
type PrivacyConcernSubreason =
  | 'sensitive-data-capture'
  | 'storage-security'
  | 'app-window-control'
  | 'workplace-compliance'
  | 'ai-training-use';

const PRIMARY_REASONS: { value: CancellationReason; label: string }[] = [
  {
    value: 'too-expensive',
    label: "It's too expensive for the value I'm getting.",
  },
  {
    value: 'not-using-enough',
    label: "I'm not using it enough to justify the cost.",
  },
  {
    value: 'ai-quality',
    label: "The AI's quality isn't meeting my expectations.",
  },
  { value: 'missing-feature', label: "It's missing a key feature I need." },
  {
    value: 'technical-problems',
    label: "I've encountered technical problems.",
  },
  {
    value: 'privacy-concerns',
    label: 'I have privacy concerns.',
  },
  { value: 'temporary-need', label: 'My need for Littlebird was temporary.' },
  { value: 'other', label: 'Other' },
];

const AI_QUALITY_SUBREASONS = [
  'The AI gives inaccurate or "made-up" answers.',
  'The answers are too generic or low-quality.',
  "It doesn't seem to have the right context about my work or dates are shown incorrectly.",
];

const PRIVACY_CONCERNS_SUBREASONS: {
  value: PrivacyConcernSubreason;
  label: string;
}[] = [
  {
    value: 'sensitive-data-capture',
    label: "I'm worried about sensitive data capture (passwords, PII, etc.)",
  },
  {
    value: 'storage-security',
    label: "I don't trust the data storage/security",
  },
  {
    value: 'app-window-control',
    label: "I can't control which apps/windows are recorded",
  },
  {
    value: 'workplace-compliance',
    label: "My workplace doesn't allow tools like this (Compliance/SOC2)",
  },
  {
    value: 'ai-training-use',
    label: "I don't want my data used for AI training",
  },
];

const FINAL_FEEDBACK_OPTIONS = [
  {
    value: 'better-performance',
    label: 'Better performance (e.g., faster, fewer bugs)',
  },
  {
    value: 'specific-features',
    label: 'Specific features (e.g., meeting notes, integrations)',
  },
  {
    value: 'higher-quality-ai',
    label:
      'Higher quality AI answers (e.g., more context-aware, more accurate)',
  },
  {
    value: 'unsure-how-to-use',
    label: "I'm not sure how to get the most out of it.",
  },
];

interface CancelSubscriptionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CancelSubscriptionModal: ModalFC<CancelSubscriptionModalProps> =
  observer(({ open, onOpenChange }) => {
    const user = userStore.user;
    const email = user?.email;
    const { setHash } = useHashRoute();
    const subscription = useSubscription();
    const [step, setStep] = useState<CancelStep>('reason');
    const [primaryReason, setPrimaryReason] =
      useState<CancellationReason | null>(null);
    const [aiQualitySubreasons, setAiQualitySubreasons] = useState<string[]>(
      []
    );
    const [privacySubreason, setPrivacySubreason] =
      useState<PrivacyConcernSubreason | null>(null);
    const [missingFeatureText, setMissingFeatureText] = useState('');
    const [finalFeedback, setFinalFeedback] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isEligibleForRetention, setIsEligibleForRetention] = useState(false);
    const [retentionOffer, setRetentionOffer] = useState<RetentionOffer>(
      FALLBACK_RETENTION_OFFER
    );
    const hasFetchedContext = useRef(false);

    useEffect(() => {
      if (!open) {
        hasFetchedContext.current = false;
        return;
      }
      if (hasFetchedContext.current) return;
      hasFetchedContext.current = true;

      subscriptionApis
        .getCancellationContext()
        .then((ctx) => {
          setIsEligibleForRetention(ctx.is_eligible_for_retention);
          if (ctx.retention_offer) setRetentionOffer(ctx.retention_offer);
        })
        .catch(captureException);
    }, [open]);

    const resetAndClose = () => {
      onOpenChange(false);
      setStep('reason');
      setPrimaryReason(null);
      setAiQualitySubreasons([]);
      setPrivacySubreason(null);
      setMissingFeatureText('');
      setFinalFeedback(null);
      setIsLoading(false);
    };

    const getFeedbackSummary = () => {
      const reasonLabel =
        PRIMARY_REASONS.find((r) => r.value === primaryReason)?.label ||
        primaryReason;
      let summary = `Primary reason: ${reasonLabel}\n`;
      if (primaryReason === 'ai-quality' && aiQualitySubreasons.length > 0) {
        summary += `\nAI Quality issues:\n${aiQualitySubreasons.map((r) => `  • ${r}`).join('\n')}\n`;
      }
      if (primaryReason === 'privacy-concerns' && privacySubreason) {
        const privacySubreasonLabel =
          PRIVACY_CONCERNS_SUBREASONS.find((r) => r.value === privacySubreason)
            ?.label || privacySubreason;
        summary += `\nPrivacy concern:\n${privacySubreasonLabel}\n`;
      }
      if (primaryReason === 'missing-feature' && missingFeatureText.trim()) {
        summary += `\nMissing feature details:\n${missingFeatureText.trim()}\n`;
      }
      if (finalFeedback) {
        const feedbackLabel =
          FINAL_FEEDBACK_OPTIONS.find((f) => f.value === finalFeedback)
            ?.label || finalFeedback;
        summary += `\nWhat would have made it more valuable: ${feedbackLabel}\n`;
      }
      return `${summary}\n\n=== Info ===\n- App version: ${__APP_VERSION__}\n`;
    };

    const submitAndCancel = async () => {
      setIsLoading(true);
      try {
        const summary = getFeedbackSummary();
        submitSurvey('feedback & support', [
          'subscription_cancellation',
          email ?? 'anonymous',
          summary,
        ]).catch(captureException);
        backendApis.feedback
          .submitFeedback(Feedback.SubscriptionCancellation, summary)
          .catch(captureException);
        await subscription.cancelSubscription();
        resetAndClose();
      } catch (error) {
        captureException(error);
        toast.error('Failed to cancel subscription. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    const applyDiscount = async () => {
      setIsLoading(true);
      try {
        const result = await subscription.applyRetentionOffer();
        if (result.success) {
          trackEvent('RETENTION_DISCOUNT_APPLIED');
          resetAndClose();
        }
      } catch (error) {
        captureException(error);
        toast.error('Failed to apply discount. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    const applyDiscountAndOpenPrivacyControls = async () => {
      await applyDiscount();
      setHash('settings/PrivacyControls');
    };

    const openPrivacyControls = () => {
      setHash('settings/PrivacyControls');
    };

    const emailSupportForCompliance = () => {
      const subject = 'Requesting compliance materials and extension';
      const body = `Hi Littlebird team,

My workplace requires compliance documentation before using tools like this.
Could you please share your security whitepaper/audit materials and help with the free 3-month Plus extension?

Thanks!`;
      handleMailTo('support@littlebird.ai', subject, body);
      resetAndClose();
    };

    const handleContinue = () => {
      if (!primaryReason) return;
      if (primaryReason === 'temporary-need' || primaryReason === 'other') {
        submitAndCancel();
      } else if (
        (primaryReason === 'too-expensive' ||
          primaryReason === 'not-using-enough') &&
        !isEligibleForRetention
      ) {
        setStep('final-feedback');
      } else {
        setStep('deflection');
      }
    };

    const toggleSubreason = (subReason: string) => {
      setAiQualitySubreasons((prev) =>
        prev.includes(subReason)
          ? prev.filter((r) => r !== subReason)
          : [...prev, subReason]
      );
    };

    const optionButtonClass = (selected: boolean) =>
      cn(
        'justify-start text-left h-auto py-2.5 px-3 border-border-strong',
        selected && 'border-accent'
      );

    const KeepButton = () => (
      <Button variant="outline" onClick={resetAndClose}>
        Keep Subscription
      </Button>
    );

    const CancelButton = ({
      onClick,
      label = 'Cancel Subscription',
    }: {
      onClick: () => void;
      label?: string;
    }) => (
      <Button
        variant="destructive"
        onClick={onClick}
        isLoading={isLoading}
        disabled={isLoading}
      >
        {label}
      </Button>
    );

    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Content className="gap-4 max-w-2xl">
          {step === 'reason' && (
            <>
              <Dialog.Header className="text-left">
                <Dialog.Title>We're sorry to see you go!</Dialog.Title>
                <Dialog.Description>
                  To help us improve, please let us know the primary reason
                  you're canceling your subscription.
                </Dialog.Description>
              </Dialog.Header>
              <YStack className="gap-2 my-4">
                <span className="text-sm font-medium text-foreground-subtle mb-2">
                  What's the main reason you're canceling?
                </span>
                {PRIMARY_REASONS.map((reason) => (
                  <Button
                    key={reason.value}
                    size="sm"
                    variant="outline"
                    className={optionButtonClass(
                      primaryReason === reason.value
                    )}
                    onClick={() => setPrimaryReason(reason.value)}
                  >
                    {reason.label}
                  </Button>
                ))}
              </YStack>
              <Dialog.Footer className="mt-2 gap-3">
                <KeepButton />
                <Button
                  variant="destructive"
                  disabled={!primaryReason || isLoading}
                  onClick={handleContinue}
                  isLoading={isLoading}
                >
                  Continue
                </Button>
              </Dialog.Footer>
            </>
          )}

          {step === 'deflection' && (
            <>
              {primaryReason === 'too-expensive' && (
                <>
                  <Dialog.Header className="text-left">
                    <Dialog.Title>A special offer just for you</Dialog.Title>
                    <Dialog.Description>
                      We'd love for you to stay. Would{' '}
                      <strong>
                        {retentionOffer.display_amount}{' '}
                        {retentionOffer.duration_label}
                      </strong>{' '}
                      help?
                    </Dialog.Description>
                  </Dialog.Header>
                  <Dialog.Footer className="mt-4 gap-3 sm:justify-start">
                    <Button
                      variant="outline"
                      onClick={() => setStep('final-feedback')}
                      disabled={isLoading}
                    >
                      No Thanks, Continue to Cancel
                    </Button>
                    <Button
                      onClick={applyDiscount}
                      isLoading={isLoading}
                      disabled={isLoading}
                    >
                      Yes, Apply Discount and Keep My Subscription
                    </Button>
                  </Dialog.Footer>
                </>
              )}

              {primaryReason === 'not-using-enough' && (
                <>
                  <Dialog.Header className="text-left">
                    <Dialog.Title>Let's help you build the habit</Dialog.Title>
                    <Dialog.Description>
                      Sometimes it takes a little while to integrate a new tool
                      into your workflow. We'd like to offer you{' '}
                      <strong>
                        {retentionOffer.display_amount}{' '}
                        {retentionOffer.duration_label}
                      </strong>
                      . We're also happy to provide a{' '}
                      <a
                        href="https://meetings-na2.hubspot.com/eric-wu/onboarding-call"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent underline"
                      >
                        1-1 training session
                      </a>
                      .
                    </Dialog.Description>
                  </Dialog.Header>
                  <Dialog.Footer className="mt-4 gap-3 sm:justify-start">
                    <Button
                      variant="outline"
                      onClick={() => setStep('final-feedback')}
                      disabled={isLoading}
                    >
                      No Thanks, I'd like to cancel
                    </Button>
                    <Button
                      onClick={applyDiscount}
                      isLoading={isLoading}
                      disabled={isLoading}
                    >
                      Yes, Apply Discount and Keep My Subscription
                    </Button>
                  </Dialog.Footer>
                </>
              )}

              {primaryReason === 'ai-quality' && (
                <>
                  <Dialog.Header className="text-left">
                    <Dialog.Title>Could you tell us a bit more?</Dialog.Title>
                    <Dialog.Description>
                      Please select all that apply (optional)
                    </Dialog.Description>
                  </Dialog.Header>
                  <YStack className="gap-2 my-4">
                    {AI_QUALITY_SUBREASONS.map((subReason) => (
                      <label
                        key={subReason}
                        className="flex items-start gap-2 cursor-pointer"
                      >
                        <Checkbox
                          checked={aiQualitySubreasons.includes(subReason)}
                          onCheckedChange={() => toggleSubreason(subReason)}
                          className="mt-0.5 bg-background-subtle"
                        />
                        <span className="text-sm text-foreground">
                          {subReason}
                        </span>
                      </label>
                    ))}
                  </YStack>
                  <Dialog.Footer className="mt-2 gap-3">
                    <KeepButton />
                    <CancelButton
                      onClick={submitAndCancel}
                      label="Submit Feedback and Cancel"
                    />
                  </Dialog.Footer>
                </>
              )}

              {primaryReason === 'missing-feature' && (
                <>
                  <Dialog.Header className="text-left">
                    <Dialog.Title>
                      What would make Littlebird indispensable for you?
                    </Dialog.Title>
                    <Dialog.Description>
                      Tell us what feature you're missing (optional)
                    </Dialog.Description>
                  </Dialog.Header>
                  <YStack className="my-4">
                    <TextArea
                      className="h-24"
                      value={missingFeatureText}
                      onChange={(e) => setMissingFeatureText(e.target.value)}
                      placeholder="Please describe the feature you need..."
                    />
                  </YStack>
                  <Dialog.Footer className="mt-2 gap-3">
                    <KeepButton />
                    <CancelButton
                      onClick={submitAndCancel}
                      label="Submit Feedback and Cancel"
                    />
                  </Dialog.Footer>
                </>
              )}

              {primaryReason === 'technical-problems' && (
                <>
                  <Dialog.Header className="text-left">
                    <Dialog.Title>Let us help fix that</Dialog.Title>
                    <Dialog.Description>
                      Our support team can often resolve issues quickly. Have
                      you had a chance to visit our{' '}
                      <a
                        href="https://support.littlebird.ai/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent underline"
                      >
                        Help Center
                      </a>
                      ?
                    </Dialog.Description>
                  </Dialog.Header>
                  <Dialog.Footer className="mt-4 gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setStep('final-feedback')}
                    >
                      Continue to Cancel
                    </Button>
                    <Button
                      onClick={() =>
                        window.open('https://support.littlebird.ai/', '_blank')
                      }
                    >
                      Contact Support
                    </Button>
                  </Dialog.Footer>
                </>
              )}

              {primaryReason === 'privacy-concerns' && (
                <>
                  {!privacySubreason && (
                    <>
                      <Dialog.Header className="text-left">
                        <Dialog.Title>
                          Which privacy concern matters most?
                        </Dialog.Title>
                        <Dialog.Description>
                          Please choose one so we can address it directly.
                        </Dialog.Description>
                      </Dialog.Header>
                      <YStack className="gap-2 my-4">
                        {PRIVACY_CONCERNS_SUBREASONS.map((reason) => (
                          <label
                            key={reason.value}
                            className={cn(
                              'flex items-start gap-2 cursor-pointer rounded-md border border-border-strong px-3 py-2.5',
                              privacySubreason === reason.value &&
                                'border-accent'
                            )}
                          >
                            <Checkbox
                              checked={privacySubreason === reason.value}
                              onCheckedChange={() =>
                                setPrivacySubreason((prev) =>
                                  prev === reason.value ? null : reason.value
                                )
                              }
                              className="mt-0.5 bg-background-subtle"
                            />
                            <span className="text-sm text-foreground">
                              {reason.label}
                            </span>
                          </label>
                        ))}
                      </YStack>
                      <Dialog.Footer className="mt-2 gap-3">
                        <KeepButton />
                        <Button
                          variant="destructive"
                          disabled={!privacySubreason || isLoading}
                          onClick={submitAndCancel}
                          isLoading={isLoading}
                        >
                          Continue to Cancel
                        </Button>
                      </Dialog.Footer>
                    </>
                  )}

                  {privacySubreason === 'sensitive-data-capture' && (
                    <>
                      <Dialog.Header className="text-left">
                        <Dialog.Title>Your privacy is built-in.</Dialog.Title>
                        <Dialog.Description>
                          Littlebird is a screen-reader, not a recorder. We use
                          automated filters to strip out passwords, incognito
                          windows, and credit card fields before they ever leave
                          your device. Plus, your data is{' '}
                          <strong>never used for AI training</strong>, and we
                          have a strict <strong>'No Human Eyes' policy</strong>{' '}
                          as well, meaning access is highly restricted to only
                          two senior engineers with real-time audit alerts
                          ensuring privacy and transparency into access.
                        </Dialog.Description>
                      </Dialog.Header>
                      {isEligibleForRetention && (
                        <Dialog.Description className="text-left">
                          Want to keep exploring with total peace of mind? Take{' '}
                          <strong>
                            {retentionOffer.display_amount}{' '}
                            {retentionOffer.duration_label}
                          </strong>{' '}
                          while you fine-tune your settings.
                        </Dialog.Description>
                      )}
                      <Dialog.Footer className="mt-4 gap-3 sm:justify-start">
                        {isEligibleForRetention && (
                          <Button
                            onClick={applyDiscount}
                            isLoading={isLoading}
                            disabled={isLoading}
                          >
                            Apply {retentionOffer.display_amount} and Keep
                            Access
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          onClick={submitAndCancel}
                          disabled={isLoading}
                        >
                          No Thanks, Continue to Cancel
                        </Button>
                      </Dialog.Footer>
                    </>
                  )}

                  {privacySubreason === 'storage-security' && (
                    <>
                      <Dialog.Header className="text-left">
                        <Dialog.Title>
                          Enterprise-grade security by default.
                        </Dialog.Title>
                        <Dialog.Description>
                          We are SOC 2 Type I Certified, and all your data is
                          encrypted (AES-256) on hardened AWS servers.{' '}
                          <strong>
                            No human at Littlebird reads your data
                          </strong>
                          ; access is highly restricted to two senior engineers
                          with real-time audit alerts. You remain in 100%
                          control to pause, delete, or export your data anytime.
                        </Dialog.Description>
                      </Dialog.Header>
                      <Dialog.Description className="text-left">
                        Here&apos;s a link to our full{' '}
                        <a
                          href="https://trust.littlebird.ai/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent underline"
                        >
                          Security &amp; Trust Center
                        </a>
                        .{' '}
                        {isEligibleForRetention && (
                          <>
                            We&apos;d love to keep earning your trust. Would{' '}
                            <strong>{retentionOffer.display_amount}</strong>{' '}
                            help you stay with us while we grow?
                          </>
                        )}
                      </Dialog.Description>
                      <Dialog.Footer className="mt-4 gap-3 sm:justify-start">
                        {isEligibleForRetention && (
                          <Button
                            onClick={applyDiscount}
                            isLoading={isLoading}
                            disabled={isLoading}
                          >
                            Apply Discount and Stay Secure
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          onClick={submitAndCancel}
                          disabled={isLoading}
                        >
                          Continue to Cancel
                        </Button>
                      </Dialog.Footer>
                    </>
                  )}

                  {privacySubreason === 'app-window-control' && (
                    <>
                      <Dialog.Header className="text-left">
                        <Dialog.Title>You decide what we see.</Dialog.Title>
                        <Dialog.Description>
                          Littlebird is a screen-reader, not a keylogger. By
                          default, we automatically ignore{' '}
                          <strong>
                            password managers, incognito windows, and hidden
                            fields
                          </strong>
                          . We also provide a set of controls to exclude
                          specific apps and web domains as well as entire
                          categories like &ldquo;Financial&rdquo; or
                          &ldquo;Healthcare.&rdquo; You can access these
                          controls in one click via Settings {'>'} Privacy
                          Controls. You are always in the driver&apos;s seat.
                        </Dialog.Description>
                      </Dialog.Header>
                      <Dialog.Description className="text-left">
                        <button
                          type="button"
                          onClick={openPrivacyControls}
                          className="text-accent underline"
                        >
                          Open My App Privacy Controls
                        </button>
                      </Dialog.Description>
                      {isEligibleForRetention && (
                        <Dialog.Description className="text-left">
                          Let&apos;s get your setup right. We&apos;ll give you{' '}
                          <strong>
                            {retentionOffer.display_amount}{' '}
                            {retentionOffer.duration_label}
                          </strong>{' '}
                          to get your exclusions perfect.
                        </Dialog.Description>
                      )}
                      <Dialog.Footer className="mt-4 gap-3 sm:justify-start">
                        {isEligibleForRetention && (
                          <Button
                            onClick={applyDiscountAndOpenPrivacyControls}
                            isLoading={isLoading}
                            disabled={isLoading}
                          >
                            Claim {retentionOffer.display_amount} and Fix My
                            Settings
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          onClick={submitAndCancel}
                          disabled={isLoading}
                        >
                          No Thanks, Continue to Cancel
                        </Button>
                      </Dialog.Footer>
                    </>
                  )}

                  {privacySubreason === 'workplace-compliance' && (
                    <>
                      <Dialog.Header className="text-left">
                        <Dialog.Title>
                          Help us win over your Security and Compliance team.
                        </Dialog.Title>
                        <Dialog.Description>
                          We know security is a non-negotiable for work.
                          Littlebird is <strong>SOC 2 Type I certified</strong>{' '}
                          and we are currently completing our{' '}
                          <strong>Type II audit</strong>. We maintain a strict
                          &apos;No Human Eyes&apos; policy and bank-grade
                          encryption (AES-256) on hardened AWS servers. Before
                          you go, could our Security Whitepaper Report help
                          unblock this with your IT team?
                        </Dialog.Description>
                      </Dialog.Header>
                      <Dialog.Description className="text-left">
                        Here&apos;s a link to our full{' '}
                        <a
                          href="https://trust.littlebird.ai/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent underline"
                        >
                          Security &amp; Trust Center
                        </a>
                        . If you&apos;re willing to help us navigate a trial for
                        your team, we&apos;re happy to offer a free 3-month Plus
                        extension while we work through the compliance
                        checklist.
                      </Dialog.Description>
                      <Dialog.Footer className="mt-4 gap-3 sm:justify-start">
                        <Button
                          onClick={emailSupportForCompliance}
                          isLoading={isLoading}
                          disabled={isLoading}
                        >
                          Email Support for Free Access and Compliance Materials
                        </Button>
                        <Button
                          variant="outline"
                          onClick={submitAndCancel}
                          disabled={isLoading}
                        >
                          No Thanks, Cancel
                        </Button>
                      </Dialog.Footer>
                    </>
                  )}

                  {privacySubreason === 'ai-training-use' && (
                    <>
                      <Dialog.Header className="text-left">
                        <Dialog.Title>
                          Zero Training. Guaranteed. Your data is not a product.
                        </Dialog.Title>
                        <Dialog.Description>
                          Littlebird{' '}
                          <strong>
                            does not use your private screen context to train
                            foundational AI models.
                          </strong>{' '}
                          We have a strict{' '}
                          <strong>'Zero Training' policy</strong>. We do not use
                          your data to improve our models. Your data is used
                          exclusively to provide <em>you</em> with answers, and
                          nothing else.
                        </Dialog.Description>
                      </Dialog.Header>
                      {isEligibleForRetention && (
                        <Dialog.Description className="text-left">
                          We want to be the AI tool you actually trust. Take{' '}
                          <strong>
                            {retentionOffer.display_amount}{' '}
                            {retentionOffer.duration_label}
                          </strong>{' '}
                          as a thank you for helping us build a privacy-first
                          company.
                        </Dialog.Description>
                      )}
                      <Dialog.Footer className="mt-4 gap-3 sm:justify-start">
                        {isEligibleForRetention && (
                          <Button
                            onClick={applyDiscount}
                            isLoading={isLoading}
                            disabled={isLoading}
                          >
                            Apply Discount and Stay with Littlebird
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          onClick={submitAndCancel}
                          disabled={isLoading}
                        >
                          No Thanks, Continue to Cancel
                        </Button>
                      </Dialog.Footer>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {step === 'final-feedback' && (
            <>
              <Dialog.Header className="text-left">
                <Dialog.Title>Thanks for the feedback</Dialog.Title>
                <Dialog.Description>
                  One last optional question: What would have made Littlebird
                  more valuable to you?
                </Dialog.Description>
              </Dialog.Header>
              <YStack className="gap-2 my-4">
                {FINAL_FEEDBACK_OPTIONS.map((option) => (
                  <Button
                    key={option.value}
                    size="sm"
                    variant="outline"
                    className={optionButtonClass(
                      finalFeedback === option.value
                    )}
                    onClick={() => setFinalFeedback(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </YStack>
              <Dialog.Footer className="mt-2 gap-3">
                <KeepButton />
                <CancelButton onClick={submitAndCancel} />
              </Dialog.Footer>
            </>
          )}
        </Dialog.Content>
      </Dialog.Root>
    );
  });
